import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import { ClerkProvider } from '@clerk/clerk-react';
import './index.css';
import App from './App';
import IntakePage, { INTAKE_PATH_RE } from './pages/IntakePage';
import { makeIsAtlasApi, makeCsrfFetch, makeClerkFetch } from './authFetch';

// window.fetch wrappers (CSRF X-CSRF-Token + Clerk JWT Authorization, with a
// one-shot 401 retry). The logic lives in ./authFetch so it can be unit-tested
// (importing this file mounts React). CRITICAL: both wrappers only ever rewrite
// Atlas /api/* requests; third-party requests (Clerk SDK, Cloudflare Turnstile,
// Stripe) pass through verbatim. See the INVARIANT note in authFetch.js — a
// previous regression that rebuilt every request's headers broke all sign-in.

// Atlas backend's origin, if the React app is served separately (npm start on
// :3000 hitting Flask on :5000). Parsed from REACT_APP_API_URL. For the
// single-origin deploy (Flask serving the built bundle), this is null and
// same-origin /api/* paths cover it.
const _BACKEND_ORIGIN = (() => {
  const raw = process.env.REACT_APP_API_URL || '';
  try {
    return raw ? new URL(raw).origin : null;
  } catch {
    return null;
  }
})();

const _isAtlasApi = makeIsAtlasApi(_BACKEND_ORIGIN, window.location.origin);

// Install order matters: the Clerk wrapper delegates to the CSRF-patched fetch,
// which delegates to the original. document.cookie / window.Clerk are read live
// on each call via the accessor thunks.
window.fetch = makeCsrfFetch(window.fetch, _isAtlasApi, () => document.cookie);
window.fetch = makeClerkFetch(window.fetch, _isAtlasApi, () => window.Clerk);

// Boot wrapper: fetches the runtime config from the backend (which holds the
// Clerk publishable key in its env), then mounts <ClerkProvider> with it. This
// avoids a separate frontend/.env file — the key lives in one place
// (backend/.env) and the React app picks it up at load.
function ClerkBoot() {
  const [publishableKey, setPublishableKey] = useState(null);
  const [error, setError] = useState(null);
  // Idle auto-logout config from the backend (env-driven). Defaults match prod.
  const [idleConfig, setIdleConfig] = useState({
    idleTimeoutMinutes: 15,
    idleWarningSeconds: 60,
  });

  useEffect(() => {
    fetch('/api/config')
      .then((r) => r.json())
      .then((data) => {
        if (!data.clerkPublishableKey) {
          throw new Error(
            'Backend returned no clerkPublishableKey. Set CLERK_PUBLISHABLE_KEY in backend/.env and restart.'
          );
        }
        setPublishableKey(data.clerkPublishableKey);
        if (data.idleTimeoutMinutes && data.idleWarningSeconds) {
          setIdleConfig({
            idleTimeoutMinutes: data.idleTimeoutMinutes,
            idleWarningSeconds: data.idleWarningSeconds,
          });
        }
      })
      .catch((err) => setError(err.message || 'Failed to load config'));
  }, []);

  if (error) {
    return (
      <div style={{ padding: 40, fontFamily: 'sans-serif', color: '#b00' }}>
        <h2>Cannot start app</h2>
        <p>{error}</p>
      </div>
    );
  }
  if (!publishableKey) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
      </div>
    );
  }
  // The host the user is on, read at runtime: app, next, staging or localhost.
  // Every Clerk URL below is ABSOLUTE on this origin. A relative "/" was not
  // enough (2026-10-22, David on next: "when I log in, still redirected to
  // app.atlasbillers.com"): Clerk resolves a relative redirect against the host
  // it performs the sign-in on, and against its dashboard Paths (absolute
  // app.atlasbillers.com URLs), so a user on next was carried to app. FORCE
  // props, not fallback ones, so the dashboard values are ignored; signInUrl /
  // signUpUrl on this origin so Clerk never sends anyone to another host to
  // sign in; allowedRedirectOrigins so Clerk accepts this origin as a redirect
  // target. Nothing here is baked at build time: the publishable key comes
  // from /api/config and the origin from window.location.
  const origin = window.location.origin;
  const home = `${origin}/`;
  // /sign-up is where an invitation link lands (the backend's INVITE_SIGN_UP_PATH):
  // App.js mounts <SignUp> there, the only component that consumes a __clerk_ticket.
  const signUp = `${origin}/sign-up`;
  return (
    <ClerkProvider
      publishableKey={publishableKey}
      signInUrl={home}
      signUpUrl={signUp}
      signInForceRedirectUrl={home}
      signUpForceRedirectUrl={home}
      afterSignOutUrl={home}
      allowedRedirectOrigins={[origin]}
    >
      <App
        idleTimeoutMinutes={idleConfig.idleTimeoutMinutes}
        idleWarningSeconds={idleConfig.idleWarningSeconds}
      />
    </ClerkProvider>
  );
}

// The public practice-intake form (docs/onboarding.md) mounts BEFORE Clerk: the practice
// filling it in has no Atlas account, the link's token is the authentication, and the
// page needs neither the Clerk SDK nor /api/config. Every other path boots as before.
const _isIntakePath = INTAKE_PATH_RE.test(window.location.pathname);

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    {_isIntakePath ? <IntakePage /> : <ClerkBoot />}
  </React.StrictMode>
);
